// Profile: edit display name, upload avatar, manage linked identities, register passkeys.
import { useEffect, useRef, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Fingerprint, ImagePlus } from "lucide-react";
import { api } from "../api/client.js";
import { useIdentities, useMe, useUpdateProfile, useUploadAvatar } from "../api/hooks.js";
import type { IdentitySummary } from "../api/types.js";
import { Avatar } from "../components/ui/Avatar.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { useLoginModal } from "../ui/loginModal.js";
import { pageVariants } from "@/lib/motion";

async function registerPasskey(): Promise<void> {
  // Uses the api client so the Authorization: Bearer header is attached (cookie-less).
  const options = await api.post<Record<string, unknown>>("/auth/passkey/register/start", {});
  const attestation = await startRegistration({ optionsJSON: options as never });
  await api.post("/auth/passkey/register/finish", { response: attestation });
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { data: me, isLoading } = useMe();
  const { openLogin } = useLoginModal();
  const qc = useQueryClient();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const identitiesQ = useIdentities(!!me);

  const [displayName, setDisplayName] = useState("");
  const [savedNote, setSavedNote] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  const [pkError, setPkError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (me) setDisplayName(me.displayName);
  }, [me]);

  if (isLoading) {
    return <div className="mx-auto max-w-2xl px-4 py-10 text-muted-foreground">{t("common.loading")}</div>;
  }

  if (!me) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-20 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">{t("profile.notSignedIn")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("profile.notSignedInHint")}</p>
        <Button variant="primary" className="mt-4" onClick={openLogin}>
          {t("nav.login")}
        </Button>
      </div>
    );
  }

  const saveName = async () => {
    const trimmed = displayName.trim();
    if (!trimmed || trimmed === me.displayName) return;
    await updateProfile.mutateAsync(trimmed);
    setSavedNote(true);
    setTimeout(() => setSavedNote(false), 1500);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadAvatar.mutate(file);
    e.target.value = "";
  };

  const identities = identitiesQ.data ?? [];
  const unlinkable = identities.length > 1;

  const unlink = async (idn: IdentitySummary) => {
    if (!unlinkable) return;
    await api.del(`/me/identities/${idn.id}`);
    qc.invalidateQueries({ queryKey: ["identities"] });
  };

  const onRegisterPasskey = async () => {
    setPkBusy(true);
    setPkError(null);
    try {
      await registerPasskey();
      qc.invalidateQueries({ queryKey: ["identities"] });
    } catch (e) {
      setPkError(e instanceof Error ? e.message : t("profile.passkeyFail"));
    } finally {
      setPkBusy(false);
    }
  };

  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="enter"
      className="mx-auto max-w-2xl px-4 py-8"
    >
      <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{t("profile.title")}</h1>

      {/* Avatar + name */}
      <Card className="mt-6">
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar src={me.avatarUrl} name={me.displayName} size={64} />
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileRef.current?.click()}
                loading={uploadAvatar.isPending}
              >
                <ImagePlus className="h-4 w-4" />
                {uploadAvatar.isPending ? t("profile.uploading") : t("profile.changeAvatar")}
              </Button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
              <p className="mt-1.5 text-xs text-muted-foreground/70">@{me.username}</p>
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="pf-name">{t("profile.displayName")}</Label>
            <div className="flex gap-2">
              <Input
                id="pf-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="flex-1"
              />
              <Button
                variant="primary"
                onClick={saveName}
                loading={updateProfile.isPending}
                disabled={!displayName.trim() || displayName.trim() === me.displayName}
              >
                {updateProfile.isPending ? t("profile.saving") : savedNote ? t("profile.saved") : t("profile.save")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Linked identities */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{t("profile.loginMethods")}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onRegisterPasskey}
            loading={pkBusy}
          >
            <Fingerprint className="h-4 w-4" />
            {pkBusy ? t("profile.waiting") : t("profile.addPasskey")}
          </Button>
        </CardHeader>
        <CardContent>
          {pkError && <p className="mb-3 text-xs text-destructive-soft">{pkError}</p>}

          <ul className="flex flex-col gap-2">
            {identitiesQ.isLoading && <li className="text-sm text-muted-foreground/70">{t("common.loading")}</li>}
            {identities.map((idn) => (
              <li
                key={idn.id}
                className="flex items-center justify-between rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground">{idn.label}</span>
                  <span className="rounded-sm bg-white/[0.08] px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                    {idn.kind === "passkey" ? "passkey" : idn.provider ?? "identity"}
                  </span>
                </div>
                <button
                  onClick={() => unlink(idn)}
                  disabled={!unlinkable}
                  title={unlinkable ? t("profile.unlink") : t("profile.keepOne")}
                  className="text-xs text-muted-foreground outline-none transition hover:text-destructive-soft disabled:cursor-not-allowed disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t("profile.unlink")}
                </button>
              </li>
            ))}
            {!identitiesQ.isLoading && identities.length === 0 && (
              <li className="text-sm text-muted-foreground/70">{t("profile.noMethods")}</li>
            )}
          </ul>
        </CardContent>
      </Card>
    </motion.main>
  );
}
